import type { PresentationData } from '@/atoms/slideBuilderAtoms';
import { scribeHttpV2 } from '@/utils/scribeHTTP';

const BASE = '/presentations';

interface PresentationApiResponse {
  id: string;
  title: string;
  slides: Array<{
    id: string;
    index: number;
    title: string;
    image_url?: string;
    notes?: string;
    source?: 'generated' | 'imported';
    extracted_text?: string;
    shape_map?: Array<{
      index: number;
      name: string;
      text: string;
      placeholder_type?: string;
    }>;
  }>;
  chat_id?: string;
  pptx_url?: string;
  preview_url?: string;
  source_type?: 'generated' | 'imported';
  available_layouts?: Array<{ name: string; placeholders: string[] }>;
  created_at: string;
  updated_at: string;
}

function toPresentationData(res: PresentationApiResponse): PresentationData {
  return {
    id: res.id,
    title: res.title,
    slides: res.slides.map((s) => ({
      id: s.id,
      index: s.index,
      title: s.title,
      imageUrl: s.image_url,
      notes: s.notes,
      source: s.source,
      extractedText: s.extracted_text,
      shapeMap: s.shape_map,
    })),
    chatId: res.chat_id ?? null,
    pptxUrl: res.pptx_url,
    previewUrl: res.preview_url,
    sourceType: res.source_type,
    availableLayouts: res.available_layouts,
  };
}

export async function fetchPresentation(id: string): Promise<PresentationData> {
  const { data } = await scribeHttpV2.get<PresentationApiResponse>(`${BASE}/${id}`);
  return toPresentationData(data);
}

export async function fetchPresentationByChatId(
  chatId: string
): Promise<PresentationData> {
  const { data } = await scribeHttpV2.get<PresentationApiResponse>(
    `${BASE}/by-chat/${chatId}`
  );
  return toPresentationData(data);
}

export async function createPresentation(params: {
  title?: string;
  chatId?: string;
}): Promise<PresentationData> {
  const { data } = await scribeHttpV2.post<PresentationApiResponse>(BASE, {
    title: params.title ?? '',
    chat_id: params.chatId,
  });
  return toPresentationData(data);
}

export async function updatePresentationTitle(
  id: string,
  title: string
): Promise<PresentationData> {
  const { data } = await scribeHttpV2.patch<PresentationApiResponse>(`${BASE}/${id}`, {
    title,
  });
  return toPresentationData(data);
}

export async function addSlide(
  presentationId: string,
  params: { title?: string; html?: string; index?: number }
): Promise<PresentationData> {
  const { data } = await scribeHttpV2.post<PresentationApiResponse>(
    `${BASE}/${presentationId}/slides`,
    params
  );
  return toPresentationData(data);
}

export async function updateSlide(
  presentationId: string,
  slideId: string,
  params: { title?: string; notes?: string }
): Promise<PresentationData> {
  const { data } = await scribeHttpV2.patch<PresentationApiResponse>(
    `${BASE}/${presentationId}/slides/${slideId}`,
    params
  );
  return toPresentationData(data);
}

export async function deleteSlide(
  presentationId: string,
  slideId: string
): Promise<PresentationData> {
  const { data } = await scribeHttpV2.delete<PresentationApiResponse>(
    `${BASE}/${presentationId}/slides/${slideId}`
  );
  return toPresentationData(data);
}

export async function reorderSlides(
  presentationId: string,
  slideIds: string[]
): Promise<PresentationData> {
  const { data } = await scribeHttpV2.post<PresentationApiResponse>(
    `${BASE}/${presentationId}/slides:reorder`,
    { slide_ids: slideIds }
  );
  return toPresentationData(data);
}

export async function deletePresentation(id: string): Promise<void> {
  await scribeHttpV2.delete(`${BASE}/${id}`);
}

export type ExportFormat = 'pptx' | 'pdf';

export async function exportPresentation(
  id: string,
  format: ExportFormat = 'pptx'
): Promise<{ download_url: string; format: string }> {
  const { data } = await scribeHttpV2.post<{ download_url: string; format: string }>(
    `${BASE}/${id}:export`,
    { format }
  );
  return data;
}
